import { useTheme } from "@material-ui/core/styles";
import { lighten, darken } from "@material-ui/core/styles/colorManipulator";
import type { Theme } from "src/theme";
import { getContrastText } from "src/utils/colorUtils";

export interface GradientStop {
  offset: string;
  color: string; // rgb(...)
}
export interface GradientType {
  name: string;
  color: string; // rgb(...)
  stops: GradientStop[];
}

export interface OrientationType {
  x1: string;
  x2: string;
  y1: string;
  y2: string;
}

export const Orientation: Record<"vertical" | "horizontal", OrientationType> = {
  vertical: {
    x1: "0%",
    x2: "0%",
    y1: "100%",
    y2: "0%",
  },
  horizontal: {
    x1: "0%",
    x2: "100%",
    y1: "0%",
    y2: "0%",
  },
};

// Default gradients (fallback)
const Gradients: GradientType[] = [
  {
    name: "Green",
    color: "rgb(4, 76, 38)",
    stops: [
      { offset: "0%", color: "rgb(24, 107, 62)" },
      { offset: "50%", color: "rgb(4, 76, 38)" },
      { offset: "100%", color: "rgb(0, 51, 51)" },
    ],
  },
  {
    name: "LightGreen",
    color: "rgb(28, 93, 59)",
    stops: [
      { offset: "0%", color: "rgb(111, 164, 137)" },
      { offset: "100%", color: "rgb(28, 93, 59)" },
    ],
  },
  {
    name: "Blue",
    color: "rgb(24, 68, 97)",
    stops: [
      { offset: "0%", color: "rgb(67, 115, 148)" },
      { offset: "60%", color: "rgb(24, 68, 97)" },
      { offset: "100%", color: "rgb(0, 51, 102)" },
    ],
  },
  {
    name: "LightBlue",
    color: "rgb(68, 112, 147)",
    stops: [
      { offset: "0%", color: "rgb(175, 200, 219)" },
      { offset: "100%", color: "rgb(68, 112, 147)" },
    ],
  },
  {
    name: "Grey",
    color: "rgb(215, 212, 219)",
    stops: [
      { offset: "0%", color: "rgb(215, 212, 219)" },
      { offset: "100%", color: "rgb(178, 169, 189)" },
    ],
  },
  {
    name: "LightGrey",
    color: "rgb(241, 238, 245)",
    stops: [
      { offset: "0%", color: "rgb(255, 255, 255)" },
      { offset: "100%", color: "rgb(241, 238, 245)" },
    ],
  },
];

export const GradientsByName: Record<string, GradientType> = Gradients.reduce(
  (a, g) => {
    a[g.name] = g;
    return a;
  },
  {}
);

// Create theme-aware gradients (basic version)
export function createThemedGradients(
  brandPrimary: string,
  brandSecondary: string,
  grey: string
): Record<string, GradientType> {
  return createThemedGradientsWithLights(
    brandPrimary,
    lighten(brandPrimary, 0.3),
    brandSecondary,
    lighten(brandSecondary, 0.3),
    grey
  );
}

// Create theme-aware gradients with explicit light variants
// Uses more pronounced lighten/darken values to match the intensity of default gradients
export function createThemedGradientsWithLights(
  brandPrimary: string,
  brandPrimaryLight: string,
  brandSecondary: string,
  brandSecondaryLight: string,
  grey: string,
  accent?: string
): Record<string, GradientType> {
  const accentColor = accent || brandPrimary;
  const accentLight = lighten(accentColor, 0.3);
  return {
    Green: {
      name: "Green",
      color: brandSecondary,
      stops: [
        { offset: "0%", color: lighten(brandSecondary, 0.4) },
        { offset: "50%", color: brandSecondary },
        { offset: "100%", color: darken(brandSecondary, 0.3) },
      ],
    },
    LightGreen: {
      name: "LightGreen",
      color: brandSecondaryLight,
      stops: [
        { offset: "0%", color: lighten(brandSecondaryLight, 0.5) },
        { offset: "100%", color: brandSecondaryLight },
      ],
    },
    Blue: {
      name: "Blue",
      color: brandPrimary,
      stops: [
        { offset: "0%", color: lighten(brandPrimary, 0.4) },
        { offset: "60%", color: brandPrimary },
        { offset: "100%", color: darken(brandPrimary, 0.3) },
      ],
    },
    LightBlue: {
      name: "LightBlue",
      color: brandPrimaryLight,
      stops: [
        { offset: "0%", color: lighten(brandPrimaryLight, 0.5) },
        { offset: "100%", color: brandPrimaryLight },
      ],
    },
    Accent: {
      name: "Accent",
      color: accentColor,
      stops: [
        { offset: "0%", color: lighten(accentColor, 0.4) },
        { offset: "50%", color: accentColor },
        { offset: "100%", color: darken(accentColor, 0.3) },
      ],
    },
    LightAccent: {
      name: "LightAccent",
      color: accentLight,
      stops: [
        { offset: "0%", color: lighten(accentLight, 0.5) },
        { offset: "100%", color: accentLight },
      ],
    },
    Grey: {
      name: "Grey",
      color: grey,
      stops: [
        { offset: "0%", color: lighten(grey, 0.15) },
        { offset: "100%", color: darken(grey, 0.2) },
      ],
    },
    LightGrey: {
      name: "LightGrey",
      color: lighten(grey, 0.3),
      stops: [
        { offset: "0%", color: lighten(grey, 0.6) },
        { offset: "100%", color: lighten(grey, 0.3) },
      ],
    },
  };
}

// Hook to get themed chart colors
export function useChartColors() {
  const theme = useTheme() as Theme;
  const { custom: palette, derived } = theme.palette.custom;
  const isDark = theme.palette.type === "dark";

  const grey = isDark ? theme.palette.grey[500] : theme.palette.grey[400];

  // Create gradients using the full color palette
  const gradients = createThemedGradientsWithLights(
    palette.brandPrimary,
    derived.brandPrimaryLight,
    palette.brandSecondary,
    derived.brandSecondaryLight,
    grey,
    palette.accent
  );

  return {
    gradients,
    brandPrimary: palette.brandPrimary,
    brandSecondary: palette.brandSecondary,
    brandPrimaryLight: derived.brandPrimaryLight,
    brandSecondaryLight: derived.brandSecondaryLight,
    accent: palette.accent,
    // Convenience colors matching the gradient names
    Green: gradients.Green.color,
    LightGreen: gradients.LightGreen.color,
    Blue: gradients.Blue.color,
    LightBlue: gradients.LightBlue.color,
    Accent: gradients.Accent.color,
    LightAccent: gradients.LightAccent.color,
    Grey: gradients.Grey.color,
    LightGrey: gradients.LightGrey.color,
    // Contrast text colors for labels on each chart color
    onGreen: getContrastText(gradients.Green.color),
    onLightGreen: getContrastText(gradients.LightGreen.color),
    onBlue: getContrastText(gradients.Blue.color),
    onLightBlue: getContrastText(gradients.LightBlue.color),
    onAccent: getContrastText(gradients.Accent.color),
    onLightAccent: getContrastText(gradients.LightAccent.color),
  };
}

export function makeBarFill(gradients: (GradientType | undefined)[]) {
  return gradients.reduce((style, gradient, i) => {
    if (gradient) {
      style[
        `& g.cartesianlayer g.plot g.barlayer.mlayer g.trace.bars:nth-child(${
          i + 1
        }) g.points g.point path`
      ] = {
        fill: `url(#Gradient${gradient.name}) !important`,
      };
    }
    return style;
  }, {});
}

// For horizontal bar charts - uses horizontal gradient orientation
export function makeHorizontalBarFill(gradients: (GradientType | undefined)[]) {
  return gradients.reduce((style, gradient, i) => {
    if (gradient) {
      style[
        `& g.cartesianlayer g.plot g.barlayer.mlayer g.trace.bars:nth-child(${
          i + 1
        }) g.points g.point path`
      ] = {
        fill: `url(#Gradient${gradient.name}-h) !important`,
      };
    }
    return style;
  }, {});
}

export function makePieFill(gradients: (GradientType | undefined)[]) {
  return gradients.reduce((style, gradient, i) => {
    if (gradient) {
      style[`& g.pielayer g.trace g.slice:nth-child(${i + 1}) path.surface`] = {
        fill: `url(#Gradient${gradient.name}) !important`,
      };
    }
    return style;
  }, {});
}

export function createDefs(
  orientation: OrientationType,
  gradients: GradientType[],
  suffix?: string
) {
  return gradients.map(({ name, color, stops }) => {
    const gradientId = [name, suffix].filter(Boolean).join("-");
    return (
      <linearGradient
        key={gradientId}
        id={`Gradient${gradientId}`}
        {...orientation}
      >
        {stops.map(({ offset, color }) => (
          <stop
            key={offset}
            offset={offset}
            style={{ stopColor: color } as any}
          />
        ))}
      </linearGradient>
    );
  });
}

const defs = createDefs(Orientation.vertical, Gradients);

function SvgGradientDef({ defs: d = defs }: { defs?: typeof defs }) {
  return (
    <svg style={{ width: 0, height: 0, position: "absolute" }}>
      <defs>{d}</defs>
    </svg>
  );
}

// Themed SVG gradient component that uses theme colors
export function ThemedSvgGradientDef() {
  const chartColors = useChartColors();
  const gradientList = Object.values(chartColors.gradients);

  // Create both vertical and horizontal gradients
  const verticalDefs = createDefs(Orientation.vertical, gradientList);
  const horizontalDefs = createDefs(Orientation.horizontal, gradientList, "h");

  return (
    <svg style={{ width: 0, height: 0, position: "absolute" }}>
      <defs>
        {verticalDefs}
        {horizontalDefs}
      </defs>
    </svg>
  );
}

export default SvgGradientDef;
